import type { CategoryRaw, ParentCategoryOption } from "~/features/admin/categories/types";

/** Every category path is rooted at this ltree label (enforced by Cnut's CategoryPath VO). */
export const CATEGORY_PATH_ROOT = "ctn";

/** One ltree label: mirrors the per-segment rule of Cnut's CategoryPath VO. */
const CATEGORY_PATH_SEGMENT_PATTERN = /^[a-z][a-zA-Z0-9_]*$/;

const CATEGORY_PATH_PATTERN = /^ctn(?:\.[a-z][a-zA-Z0-9_]*)*$/;

export function isValidCategoryPath(path: string): boolean {
  return CATEGORY_PATH_PATTERN.test(path);
}

/** "ctn.event.war" → { parentPath: "ctn.event", segment: "war" }; "ctn" → { parentPath: null, segment: "ctn" }. */
export function splitCategoryPath(path: string): { parentPath: string | null; segment: string } {
  const parts = path.split(".");
  const segment = parts[parts.length - 1] ?? "";
  return { parentPath: parts.length > 1 ? parts.slice(0, -1).join(".") : null, segment };
}

export function buildCategoryPath(parentPath: string | null, segment: string): string {
  return parentPath ? `${parentPath}.${segment}` : segment;
}

/**
 * Derive a path segment from a display name ("Ancient Rome" → "ancient_rome").
 * Returns "" when nothing usable remains (e.g. a Japanese-only name) so the
 * caller can leave the field for the user to fill in.
 */
export function toPathSegment(name: string): string {
  const cleaned = name
    .trim()
    .replace(/[^A-Za-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
  return CATEGORY_PATH_SEGMENT_PATTERN.test(cleaned) ? cleaned : "";
}

/** True when `path` is `ancestorPath` itself or lies underneath it (dot-boundary enforced). */
function isSelfOrDescendantPath(path: string, ancestorPath: string): boolean {
  return path === ancestorPath || path.startsWith(`${ancestorPath}.`);
}

interface BuildParentCategoryOptionsArgs {
  categories: readonly CategoryRaw[];
  /** Exclude this path and everything under it — a category cannot be parented into its own subtree. */
  excludeSubtreeOf?: string | null;
}

/**
 * Active categories with a path, ordered depth-first by path so the picker
 * reads like the hierarchy table (parent, then its children, …).
 */
export function buildParentCategoryOptions({
  categories,
  excludeSubtreeOf,
}: BuildParentCategoryOptionsArgs): ParentCategoryOption[] {
  return (
    categories
      .flatMap((category) => {
        const path = category.categoryPath?.path;
        if (!path || category.deactivatedAt) return [];
        if (excludeSubtreeOf && isSelfOrDescendantPath(path, excludeSubtreeOf)) return [];
        return [{ id: category.id, name: category.name, path, depth: path.split(".").length - 1 }];
      })
      // Plain code-point order, not localeCompare: "." sorts below every label character, which is what keeps
      // a parent's subtree contiguous ("ctn.event.war" before "ctn.event_log"); localeCompare would interleave them.
      .sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0))
  );
}

/**
 * The category whose property schemas a child at `parentPath` inherits from:
 * `parentPath` itself when registered, otherwise its nearest registered ancestor
 * (intermediate paths may have no category row of their own).
 */
export function findNearestRegisteredAncestor(
  parentPath: string | null,
  options: readonly ParentCategoryOption[],
): ParentCategoryOption | null {
  const byPath = new Map(options.map((option) => [option.path, option]));
  let current = parentPath;
  while (current) {
    const found = byPath.get(current);
    if (found) return found;
    current = splitCategoryPath(current).parentPath;
  }
  return null;
}
